import { HttpClient, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { LocalStorage } from "ngx-store";
import { ToastrService } from 'ngx-toastr';
import { fromEvent, ReplaySubject, Subject } from "rxjs";
import { filter, first, pluck, switchMap, switchMapTo, takeUntil, tap } from "rxjs/operators";
import { Bill } from "src/app/model/bills.model";
import { creditNoteAllocation } from "src/app/model/creditnote.model";
import { Url } from "src/app/model/users.model";
import { Integration } from "../integration";
import { AccountsMapping } from "./models/account-mapping";
import { Account } from "./models/accounts";
import { Bill as XeroBill } from "./models/bills";

@Injectable({
  providedIn: 'root'
})
export class XeroService implements OnDestroy {
  @ViewChild('xeroReconnectPrompt') xeroReconnectModal: TemplateRef<boolean>;
  /**
   * Application host URL.
   */
  @LocalStorage() private url: Url;

  /**
   * HTTP entry route to do Xero-related operations.
   */
  private xeroRoute: string;

  /**
   * Connection status; `true` if the user has their iDrv account
   * connected to Xero, `false` otherwise.
   */
  private _status$: ReplaySubject<boolean>;

  /**
   * Xero connection information.
   */
  private _integrationInfo$ = new ReplaySubject<Integration>(1);

  public readonly integrationInfo$ = this._integrationInfo$.asObservable();

  /**
   * Indicator used to unsubscribe to everything when the service gets destroyed.
   */
  private isDestroyed$: Subject<boolean>;

  public constructor(private client: HttpClient,private toast: ToastrService, private modal: NgbModal, ) {
    this.xeroRoute         = `${this.url.rest}/api/integrations/xero`;
    this._status$          = new ReplaySubject(1);
    this.isDestroyed$      = new Subject();

    this._integrationInfo$
      .pipe(
        pluck('connection', 'is_connected'),
        takeUntil(this.isDestroyed$)
      )
      .subscribe((status) => this._status$.next(status));

    this.loadIntegrationInfo()
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe();
  }

  public ngOnDestroy() {
    this._status$.complete();
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  /**
   * Observe the Xero connection status.
   */
  public get status$() {
    return this._status$;
  }

  /**
   * Relay authentication code to the server, as part of the
   * OAuth 2.0 Code flow.
   * 
   * @param code Authentication code.
   */
  public relayCode(code: string) {
    return this.client
      .post<Integration>(`${this.xeroRoute}`, { code })
      .pipe(tap((info) => this._integrationInfo$.next(info)));
  }

  /**
   * Connect to Xero on the sideline.
   * 
   * 
   */
  public connect() {
    console.info('Client initiates OAuth 2.0 connection to Xero.');
    return this.integrationInfo$
      .pipe(
        switchMap((config) => {
          let xeroAuthUri = `${config.oauth.issuer}`
            + `?response_type=${config.oauth.response_type}`
            + `&client_id=${config.oauth.client_id}`
            + `&redirect_uri=${config.oauth.redirect_uri}`
            + `&scope=${config.oauth.scope}`;
          
          window.open(xeroAuthUri, 'Connect iDrv to Xero');
          console.info('An external window is open. Please don not close the current window.');

          return fromEvent<MessageEvent>(window, 'message')
            .pipe(
              filter((event) => event.origin === window.location.origin),
              filter((event) => event.data?.connected !== undefined),
              first(),
              switchMap((event: MessageEvent<{ code: string }>) => {
                console.debug('Data is received from external window.', event.data);
                return this.integrationInfo$;
              }),
            );
        }),
        takeUntil(this.isDestroyed$)
      );
  }

  /**
   * Disconnect iDrv from Xero.
   * @returns Xero integration info, including connection status.
   */
  public disconnect() {
    return this.integrationInfo$
      .pipe(
        switchMap((config) => {
          return this.client
            .delete(config.oauth.disconnect_uri, {
              observe: 'response'
            })
            .pipe(
              tap((response: HttpResponse<unknown>) => {
                if(response.ok) {
                  console.info('Server disconnects user from Xero.');
                  config.connection.is_connected = false;
                  config.connection.user = null;
                  this._integrationInfo$.next(config);
                }
                else {
                  console.error('Server fails to disconnect user from Xero.');
                }
              })
            );
        }),
        switchMapTo(this.integrationInfo$),
        takeUntil(this.isDestroyed$)
      );
  }

  /**
   * Fetch all needed Xero integration information.
   */
  public loadIntegrationInfo() {
    return this.client
      .get<Integration>(`${this.xeroRoute}`)
      .pipe(tap((info) => this._integrationInfo$.next(info)));
  }

  /**
   * Create system invoices in Xero.
   * 
   * @param {Array.<number>} ids System invoice ids.
   */
  public createInvoices(ids: number[]) {
    let payload = { invoices: ids };
    return this.client.post(`${this.xeroRoute}/invoices`, payload, {
      observe: 'response'
    });
  }

  /**
   * Update invoices in Xero with the current information
   * of the system invoices whose IDs are given in the
   * arguments.
   * 
   * @param {Array.<number>} ids System invoice ids.
   */
  public updateInvoices(ids: number[]) {
    let payload = { invoices: ids };
    return this.client.put(`${this.xeroRoute}/invoices`, payload, {
      observe: 'response'
    });
  }

  /**
   * Create system credit note in Xero.
   * 
   * @param {Array.<number>} ids System credit note ids.
   */
  public createCreditNote(ids: number[]) {
    let payload = { creditNote: ids };
    return this.client.post(`${this.xeroRoute}/createCreditNote`, payload, {
      observe: 'response'
    });
    }

  /**
  * Update / Void system credit note in Xero.
  * 
  * @param {Array.<number>} ids System credit note ids.
  */
  public updateCreditNote(ids: number[]) {
    let payload = { creditNote: ids };
    return this.client.put(`${this.xeroRoute}/updateCreditNote`, payload, {
      observe: 'response'
    });
  }

  /**
  * Delete system credit note in Xero.
  * 
  * @param {Array.<number>} ids System credit note ids.
  */
  public deleteCreditNote(ids: number[]) {
    let payload = { creditNote: ids };
    return this.client.put(`${this.xeroRoute}/deleteCreditNote`, payload, {
      observe: 'response'
    });
  }

  /**
  * Allocate system credit note in Xero.
  * 
  * @param {creditNoteAllocation} allocate invoices for allocation on credit note.
  */
     public allocateCreditNote(allocate: creditNoteAllocation) {
      return this.client.put(`${this.xeroRoute}/allocateCreditNote`, allocate, {
        observe: 'response'
    });
  }

  /**
   * Push a bill to Xero for syncing.
   * 
   * @param {Bill} bill And existing system bill.
   */
  public createBill(bill: Bill) {
    return this.client.put(`${this.xeroRoute}/bills/${bill.id}`, null);
  }

  /**
   * Get Xero Chart of Accounts.
   */
  public getAccounts(params?: any) {
    return this.client
      .get<Account[]>(`${this.xeroRoute}/accounts`, { params });
  }

  /**
   * Tell iDrv which Xero accounts to associate its operations to.
   * 
   * @param mapping A map of each iDrv operation to a Xero account code.
   */
  public setAccountMapping(mapping: AccountsMapping) {
    return this.client
      .post(`${this.xeroRoute}/mapping/accounts`, mapping);
  }

  /**
   * Get the chart of accounts' mapping of Xero accounts to iDrv operations.
   */
  public getAccountMapping() {
    return this.client
      .get<AccountsMapping>(`${this.xeroRoute}/mapping/accounts`);
  }

  /**
   * Get the Xero information of a Xero bill that is synced to the
   * given system bill.
   * 
   * @param billId ID of the system bill.
   */
  public getBill(billId: number) {
    return this.client
      .get<XeroBill>(`${this.xeroRoute}/bills/${billId}`);
  }


  public handleCreditNoteFailed(response: HttpErrorResponse, status: string) {
    let verbStatus = null
    verbStatus = convertToVerb(status)
    switch (response.status) {
      case 401:
        this.toast.error(
          'It seems you are locked out of the Admin Portal. Please log back in first, ' +
          'then re-save to persist the changes to Xero.',
          `The Credit Note isn't ${status} in Xero`, {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        break;
      case 403:
        this.toast.error(
          'Your connection to Xero has expired.' +
          '',
          `The Credit Note isn't ${status} in Xero`, {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        console.error("The user is disconnected from Xero.", response.error);
        break;
      case 502 || 500:
        this.toast.error(
          `You might be experiencing incompatibility issues with ${verbStatus} the ` +
          'Credit Note to Xero. See the console for details.',
          `The Credit Note isn't ${status} in Xero`, {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        console.error(`Credit Note didn't get ${status} in Xero.`, response.error);
        break;
      default:
        this.toast.warning(
          'This application was not able handle the response back from the server. ' +
          'Please contact Logicor support team to look into this.',
          "Something's not right", {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        console.warn('Client is unable to handle server response.', response);
    }
  }

  public handleCreditNoteSuccess(response: HttpResponse<Object>, status: string) {
    switch (response.status) {
      case 204:
        this.toast.success('', `Credit Note is ${status} in Xero.`, {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        });
        console.info(`Credit Note is ${status} in Xero.`);
        break;
      case 200:
        this.toast.warning(
          `The ${status} Credit Note in Xero might not sync well for some reason. See the console for details.`,
          `The Credit Note is ${status} in Xero with warnings`, {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        console.warn(`Credit Note is ${status} in Xero with warnings.`, response.body);
        break;
      default:
        this.toast.warning(
          'This application was not able handle the response back from the server. ' +
          'Please contact Logicor support team to look into this.',
          "Something's not right", {
          timeOut: 5000,
          positionClass: 'toast-top-full-width',
          progressBar: true
        }
        );
        console.warn('Client is unable to handle server response.', response);
    }
  }

}

function convertToVerb(word: string) {
  let formatted = null
  if (word === 'updated') {
    formatted = 'updating'
  }
  else if (word === 'created') {
    formatted = 'creating'
  }
  else if (word === 'deleted') {
    formatted = 'deleting'
  }
  else if (word === 'voided') {
    formatted = 'voiding'
  }
  else if (word === 'allocated') {
    formatted = 'allocating'
  }
  return formatted
}